import { TransitionHookScope } from '@uirouter/angularjs';
import * as angular from 'angular';
import { CurrentUserService } from 'services/currentUser';

export const module = angular.module('flagshipService', []);

const FLAGSHIP_DECISION_URL = 'https://decision.flagship.io/v2/';
const CONTEXT_HIPPO_PROJECT_KEY = 'HIPPO_PROJECT';
const CONTEXT_HIPPO_PROJECT_VALUE = 'customer-portal';
const FLAG_MOBILE_APP_DOWNLOAD = 'mobileAppDownloadRightRail';

export const ALL_FLAGS: Array<FeatureFlag> = [
    'ReactChecklistPage',
    'ReactClaimsPage',
    'ReactCoveragePage',
    'DocumentsReactPage',
    'ReactGetMorePage',
    'mobileAppDownloadRightRail',
    'mobileAppDownloadLoginModal',
    'ReactOverviewPage',
    'ReactPolicyList',
    'ReactLoginPage'
];

export class FlagshipService {
    private $http: angular.IHttpService;
    private $q: angular.IQService;
    private $window: angular.IWindowService;
    private $rootScope: angular.IRootScopeService;
    private currentUserService: CurrentUserService;
    private visitorId: string;
    private featureFlags: FeatureFlagStore;
    featureFlagStatus: FeatureFlagStatus;

    constructor(
        $http: angular.IHttpService,
        $q: angular.IQService,
        $window: angular.IWindowService,
        $rootScope: angular.IRootScopeService,
        currentUserService: CurrentUserService
    ) {
        this.$http = $http;
        this.$q = $q;
        this.$window = $window;
        this.$rootScope = $rootScope;
        this.visitorId = '';
        this.currentUserService = currentUserService;
        this.currentUserService.getDisplayOrderedCustomerPolicyInfos().then((policyInfos) => {
            this.visitorId = policyInfos[0].personal_information.email;
        });
        this.featureFlags = this.getInitialFlags();
        this.featureFlagStatus = 'FeatureFlagStatus:Initial';
    }

    getInitialFlags(): FeatureFlagStore {
        const store: FeatureFlagStore = {};
        ALL_FLAGS.forEach((flag) => {
            store[flag] = false;
        });
        return store;
    }

    getFlagshipConfig(): FlagshipConfig | null {
        if (!this.$window.appConfig.CUSTOMER_FLAGSHIP_ENV_ID || !this.$window.appConfig.CUSTOMER_FLAGSHIP_API_KEY) {
            return null;
        }
        return {
            envId: this.$window.appConfig.CUSTOMER_FLAGSHIP_ENV_ID,
            apiKey: this.$window.appConfig.CUSTOMER_FLAGSHIP_API_KEY
        };
    }

    getFlagshipBaseUrl(): string | null {
        const config = this.getFlagshipConfig();
        if (!config) {
            return null;
        }
        return `${FLAGSHIP_DECISION_URL}${config.envId}/`;
    }

    getCampaigns(context?: Record<string, string>): angular.IPromise<angular.IHttpResponse<FlagshipCampaignResponse>> {
        const config = this.getFlagshipConfig();
        if (!config) {
            return Promise.reject();
        }

        this.featureFlagStatus = 'FeatureFlagStatus:Fetching';

        const url = `${this.getFlagshipBaseUrl()}campaigns?mode=normal&exposeAllKeys=true&sendContextEvent=false`;
        return this.$http<FlagshipCampaignResponse>({
            method: 'POST',
            url,
            headers: {
                'x-api-key': config.apiKey
            },
            data: {
                visitorId: this.visitorId,
                context: {
                    ...context,
                    [CONTEXT_HIPPO_PROJECT_KEY]: CONTEXT_HIPPO_PROJECT_VALUE
                }
            }
        }).then((res) => {
            if (res && res.data) {
                const { campaigns } = res.data;
                campaigns.forEach((campaign) => {
                    ALL_FLAGS.forEach((flag) => {
                        if (campaign.variation.modifications.value[flag] !== undefined) {
                            this.featureFlags[flag] = campaign.variation.modifications.value[flag];
                        }
                    });
                });
                this.featureFlagStatus = 'FeatureFlagStatus:Ready';
                this.$rootScope.$broadcast('FeatureFlagStatus:Ready');
            }
            return res;
        });
    }

    getCampaignsForStates(states?: string): angular.IPromise<angular.IHttpResponse<FlagshipCampaignResponse>> {
        if (states) {
            return this.getCampaigns({ state: states.toLowerCase() });
        }
        const stateMap: Record<string, boolean> = {};
        return this.currentUserService.getDisplayOrderedCustomerPolicyInfos().then((policies) => {
            policies.forEach((policyInfo) => {
                const state = policyInfo.property_data.address.state;
                if (!stateMap[state]) {
                    stateMap[state] = true;
                }
            });
            const stateString = Object.keys(stateMap).join(',');
            return this.getCampaigns({ state: stateString.toLowerCase() });
        });
    }

    getFeatureFlagStatus(): FeatureFlagStatus {
        return this.featureFlagStatus;
    }

    getFlag(flag: FeatureFlag): FlagshipModification {
        return this.featureFlags[flag] || false;
    }

    async isMobileAppDownloadFlagEnabled(): Promise<boolean> {
        const stateMap: Record<string, boolean> = {};
        const policies = await this.currentUserService.getDisplayOrderedCustomerPolicyInfos();
        policies.forEach((policyInfo) => {
            const state = policyInfo.property_data.address.state;
            if (!stateMap[state]) {
                stateMap[state] = true;
            }
        });
        const stateString = Object.keys(stateMap).join(',');
        const response = await this.getCampaignsForStates(stateString);
        if (response && response.data) {
            const { campaigns } = response.data;
            return campaigns.some((campaign) => {
                if (campaign.variation.modifications.value[FLAG_MOBILE_APP_DOWNLOAD]) {
                    return true;
                }
            });
        }
        return false;
    }
}

module.factory('flagshipService', function(
    $http: angular.IHttpService,
    $q: angular.IQService,
    $window: angular.IWindowService,
    $rootScope: angular.IRootScopeService,
    currentUserService: CurrentUserService
) {
    'ngInject';

    return new FlagshipService($http, $q, $window, $rootScope, currentUserService);
});
